import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { Router, RouterLink } from '@angular/router';
import { AuthManager } from '@infra-adapters/services/auth.service';
import { RegistrationReviewNotificationsService } from '@infra-adapters/services/registration-review-notifications.service';
import { SessionExpiryRedirectService } from '@infra-adapters/services/session-expiry-redirect.service';
import { ProfileAvatarUiService } from '@infra-adapters/services/profile-avatar.ui.service';
import { MedicalRecordBackofficePermissionsPolicy } from '@core-rules/medical-record/medical-record-backoffice-permissions.policy';
import {
  AvatarComponent,
  BadgeComponent,
  BreadcrumbRouterComponent,
  ContainerComponent,
  DropdownComponent,
  DropdownDividerDirective,
  DropdownHeaderDirective,
  DropdownItemDirective,
  DropdownMenuDirective,
  DropdownToggleDirective,
  HeaderComponent,
  HeaderNavComponent,
  HeaderTogglerDirective,
  SidebarToggleDirective,
  TextColorDirective,
  ThemeDirective,
  ColorModeService,
} from '@coreui/angular-pro';

import { IconDirective } from '@coreui/icons-angular';
import { BannerNewsService } from '@infra-adapters/services/banner-news.service';
import { NotificationApiService } from '@infra-adapters/services/notification.api.service';
import {
  RBAC_PERMISSIONS,
} from '@core-interfaces/rbac/rbac-permission-codes';
import { sessionHasPermission } from '@core-interfaces/rbac/session-permissions';

@Component({
  selector: 'app-default-header',
  templateUrl: './default-header.component.html',
  imports: [
    ContainerComponent,
    HeaderTogglerDirective,
    SidebarToggleDirective,
    IconDirective,
    HeaderNavComponent,
    RouterLink,
    NgTemplateOutlet,
    BreadcrumbRouterComponent,

    ThemeDirective,
    DropdownComponent,
    DropdownToggleDirective,
    TextColorDirective,
    AvatarComponent,
    DropdownMenuDirective,
    DropdownHeaderDirective,
    DropdownItemDirective,
    BadgeComponent,
    DropdownDividerDirective,
  ],
})
export class DefaultHeaderComponent extends HeaderComponent implements OnInit {
  readonly #colorModeService = inject(ColorModeService);
  readonly #authManager = inject(AuthManager);
  readonly #router = inject(Router);
  readonly #sessionExpiry = inject(SessionExpiryRedirectService);
  readonly #regNotifications = inject(RegistrationReviewNotificationsService);
  readonly #profileAvatar = inject(ProfileAvatarUiService);
  readonly #prestacionPerms = inject(MedicalRecordBackofficePermissionsPolicy);
  readonly #bannerNews = inject(BannerNewsService);
  readonly #notificationApi = inject(NotificationApiService);

  readonly #unreadNotifCount = signal(0);
  readonly #destroyRef = inject(DestroyRef);
  readonly unreadNotifCount = computed(() => this.#unreadNotifCount());

  readonly canValidatePrestaciones = computed(() =>
    this.#prestacionPerms.canValidatePrestacionesFromSession(
      this.#authManager.UserSessionData(),
    ),
  );
  readonly canReadNotifications = computed(() =>
    sessionHasPermission(
      this.#authManager.UserSessionData(),
      RBAC_PERMISSIONS.NOTIFICATIONS_READ,
    ),
  );
  readonly showBellNotifications = computed(
    () => this.canValidatePrestaciones() || this.canReadNotifications(),
  );
  readonly bellBadgeCount = computed(() => {
    let n = 0;
    if (this.canValidatePrestaciones()) {
      n += this.#regNotifications.prestacionRecentAlerts().length;
    }
    if (this.canReadNotifications()) {
      n += this.#unreadNotifCount();
    }
    return n;
  });
  readonly prestacionRecentAlerts = this.#regNotifications.prestacionRecentAlerts;
  readonly colorMode = this.#colorModeService.colorMode;

  readonly avatarSrc = this.#profileAvatar.avatarSrc;

  readonly newsBanners = this.#bannerNews.banners;
  readonly newsUnseenCount = this.#bannerNews.unseenCount;

  readonly userLabel = computed(() => {
    const s = this.#authManager.UserSessionData();
    if (!s) return '';
    return (s.name || s.email || s.username || '').trim();
  });

  readonly userInitials = computed(() => {
    const s = this.#authManager.UserSessionData();
    const raw = (s?.name ?? s?.username ?? s?.email ?? '').trim();
    if (!raw) return '?';
    const parts = raw.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (
        parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
      ).toUpperCase();
    }
    return raw.slice(0, 2).toUpperCase();
  });

  ngOnInit(): void {
    void this.#profileAvatar.refresh();
    void this.#bannerNews.refresh();
    void this.#loadUnreadNotifCount();
    interval(30_000)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        void this.#loadUnreadNotifCount();
      });
  }

  async #loadUnreadNotifCount(): Promise<void> {
    if (!this.#authManager.isAuth() || !this.canReadNotifications()) {
      this.#unreadNotifCount.set(0);
      return;
    }
    try {
      const count = await this.#notificationApi.getUnreadCount();
      this.#unreadNotifCount.set(count);
    } catch {
      // No bloquear la carga del header si falla
    }
  }

  logout(): void {
    this.#profileAvatar.clear();
    this.#sessionExpiry.clearWatch();
    this.#regNotifications.disconnect();
    this.#authManager.cleanSession();
    this.#router.navigate(['/login']);
  }

  onPrestacionAlertClick(id: string): void {
    this.#regNotifications.removePrestacionAlert(id);
  }

  goToAllNotifications(): void {
    void this.#router.navigate(['/notificaciones']);
  }

  constructor() {
    super();
    this.#colorModeService.localStorageItemName.set(
      'coreui-pro-angular-admin-template-theme-modern'
    );
    this.#colorModeService.eventName.set('ColorSchemeChange');
  }

  @Input() sidebarId: string = 'sidebar1';
}
